import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
    Divider,
    TextField
} from '@mui/material';
import { headingBlue, yellowish, red, orange, textGeen, green } from '../../assets/colors';
import { buttonStyle } from '../../utils/commonCSS';
import HttpService from '../../backend/requestApis';
import { fetchRegistrationInviteDetails, reviewRegistration } from '../../backend/backendApis';
import { useNavigate } from 'react-router-dom';
import Loader from '../Loader';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../CustomComponents/CustomComponentsSlice';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const getStatusProps = (status) => {
  switch (status) {
    case 'generated': return { label: 'Generated', color: yellowish };
    case 'expired': return { label: 'Expired', color: red };
    case 'exhausted': return { label: 'Exhausted', color: orange };
    case 'underVerification': return { label: 'Under Verification', color: textGeen };
    case 'verified': return { label: 'Verified', color: green };
    case 'rejected': return { label: 'Rejected', color: red };
    default: return { label: status || '-', color: 'inherit' };
  }
};

const RegistrationInviteDetails = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pdfRef = useRef(null);
    const [inviteDetails, setInviteDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    useEffect(() => {
        if (props.dialogStatus && props.data) {
            const getDetails = async () => {
                setIsLoading(true);
                try {
                    let options = {
                        url: fetchRegistrationInviteDetails,
                        method: 'GET',
                        params: { id: props.data._id },
                        headers: {
                            Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                        }
                    };
                    let response = await HttpService(options);
                    console.log(response);
                    if (response?.data?.status === 1 || response?.data?.data) {
                        setInviteDetails(response?.data?.data || response?.data);
                    }
                } catch (error) {
                    console.error('Error fetching invite details:', error);
                    let errorMsg = error?.response?.data?.description || error?.response?.data?.message || error?.message || 'Error fetching details';
                    dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
                    if (error?.response?.status === 403 || error?.status === 403) {
                        localStorage.clear();
                        window.location.href = '/Login';
                        return;
                    }
                } finally {
                    setIsLoading(false);
                }
            };
            getDetails();
        } else {
            setInviteDetails(null);
        }
    }, [props.dialogStatus, props.data, navigate]);

    const titleStyle = {
        backgroundColor: headingBlue,
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 0rem 0.4rem 1rem"
    };

    const handleAcceptConfirm = async () => {
        setAcceptDialogOpen(false);
        setIsLoading(true);
        let success = false;
        try {
            let options = {
                url: `${reviewRegistration}/${props.data._id}`,
                method: 'PUT',
                payload: { status: 'verified' },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };
            await HttpService(options);
            success = true;
        } catch (error) {
            console.error('Error accepting registration:', error);
            let errorMsg = error?.response?.data?.description || error?.response?.data?.message || error?.message || 'Error accepting registration';
            dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
            if (error?.response?.status === 403 || error?.status === 403) {
                localStorage.clear();
                window.location.href = '/Login';
                return;
            }
        } finally {
            setIsLoading(false);
            props.updateDialogStatus(false, success);
        }
    };

    const handleRejectConfirm = async () => {
        setRejectDialogOpen(false);
        setIsLoading(true);
        let success = false;
        try {
            let options = {
                url: `${reviewRegistration}/${props.data._id}`,
                method: 'PUT',
                payload: { status: 'rejected', rejectReason },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };
            await HttpService(options);
            success = true;
        } catch (error) {
            console.error('Error rejecting registration:', error);
            let errorMsg = error?.response?.data?.description || error?.response?.data?.message || error?.message || 'Error rejecting registration';
            dispatch(showSnackBar({ message: errorMsg, severity: 'error' }));
            if (error?.response?.status === 403 || error?.status === 403) {
                localStorage.clear();
                window.location.href = '/Login';
                return;
            }
        } finally {
            setIsLoading(false);
            setRejectReason('');
            props.updateDialogStatus(false, success);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            setIsLoading(true);
            const element = pdfRef.current;
            const canvas = await html2canvas(element, { scale: 2, useCORS: true });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            
            const margin = 10; // 10mm margins
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            
            const pdfWidth = pageWidth - (margin * 2);
            const pdfPrintableHeight = pageHeight - (margin * 2);
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            
            let heightLeft = imgHeight;
            let position = margin;

            pdf.addImage(imgData, 'PNG', margin, position, pdfWidth, imgHeight);
            heightLeft -= pdfPrintableHeight;

            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, pageHeight - margin, pageWidth, margin, 'F');

            while (heightLeft > 0) {
                position = position - pdfPrintableHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', margin, position, pdfWidth, imgHeight);
                heightLeft -= pdfPrintableHeight;
                
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, pageWidth, margin, 'F');
                pdf.rect(0, pageHeight - margin, pageWidth, margin, 'F');
            }

            pdf.save(`${props.data?.name || 'Registration'}_Form.pdf`);
        } catch (error) {
            console.error('Error generating PDF:', error);
            dispatch(showSnackBar({ message: 'Error generating PDF', severity: 'error' }));
        } finally {
            setIsLoading(false);
        }
    };

    const isActionDisabled = props.data?.status === 'verified' || props.data?.status === 'reject' || props.data?.status === 'rejected';

    return (
        <>
            <Dialog
            maxWidth="md"
            fullWidth
            open={props.dialogStatus}
            onClose={() => props.updateDialogStatus(false)}
        >
            <DialogTitle style={titleStyle}>
                Registration Invite Details
            </DialogTitle>
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 3 }, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                <Box sx={{ pt: { xs: 0.5, sm: 2 } }}>
                    {isLoading && <Loader />}
                    <Box ref={pdfRef} sx={{ backgroundColor: '#fff', p: 1 }}>
                        {props.data && (
                            <Grid container spacing={2}>
                                <Grid item xs={4} sm>
                                    <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                                <Typography variant="body1">{props.data.name || '-'}</Typography>
                            </Grid>
                            <Grid item xs={4} sm>
                                <Typography variant="subtitle2" color="textSecondary">Contact Number</Typography>
                                <Typography variant="body1">{props.data.contactNumber || '-'}</Typography>
                            </Grid>
                            <Grid item xs={4} sm>
                                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                                <Typography variant="body1" sx={{ color: getStatusProps(props.data.status).color, fontWeight: 500 }}>
                                    {getStatusProps(props.data.status).label}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm>
                                <Typography variant="subtitle2" color="textSecondary">Generated By</Typography>
                                <Typography variant="body1">{props.data.generatedBy || '-'}</Typography>
                                <Typography variant="body2" color="textSecondary">{props.data.generatedByCN || '-'}</Typography>
                            </Grid>
                            <Grid item xs={6} sm>
                                <Typography variant="subtitle2" color="textSecondary">Reviewed By</Typography>
                                <Typography variant="body1">{props.data.reviewedBy || '-'}</Typography>
                                <Typography variant="body2" color="textSecondary">{props.data.reviewedByCN || '-'}</Typography>
                            </Grid>
                        </Grid>
                        )}
                        {inviteDetails && !isLoading && (
                            <Box sx={{ mt: 2 }}>
                                <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="subtitle1" sx={{ color: headingBlue, fontWeight: 'bold', mb: 1 }}>Personal Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.gender || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.dob ? String(inviteDetails.formDetails?.dob).split('T')[0] : '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.email || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Aadhaar Number</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.adhaarNumber || '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: headingBlue, fontWeight: 'bold', mb: 1, mt: 2 }}>Other Details</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Father's Name</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.fathersName || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">Father's Occupation</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.fathersOccupation || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">State</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.state || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Typography variant="subtitle2" color="textSecondary">District</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.district || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.address || '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: headingBlue, fontWeight: 'bold', mb: 1, mt: 2 }}>Education & Profession</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Qualification</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.qualification || '-'}</Typography>
                                </Grid>
                                <Grid item xs={6} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Profession</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.profession || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">Office Contact</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.officeContact || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">Office Address</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.officeAddress || '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: headingBlue, fontWeight: 'bold', mb: 1, mt: 2 }}>About</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Goal in Life</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.goal || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Hobbies</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.hobbies || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Extra Curricular Activities</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.extraCurricularActivities || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">Reason for Joining</Typography>
                                    <Typography variant="body2">{inviteDetails.formDetails?.reasonForJoining || '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" sx={{ color: headingBlue, fontWeight: 'bold', mb: 1, mt: 2 }}>Documents & Images</Typography>
                            <Grid container spacing={2}>
                                {['photographLink', 'signature', 'adhaarLink'].map((imgType) => (
                                    <Grid item xs={12} sm={4} key={imgType} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Typography variant="subtitle2" sx={{ textTransform: 'capitalize', mb: 1 }}>
                                            {imgType === 'adhaarLink' ? 'Aadhaar' : imgType === 'photographLink' ? 'Photograph' : 'Signature'}
                                        </Typography>
                                        {inviteDetails?.images?.[imgType] ? (
                                            <>
                                                <Box
                                                    component="img"
                                                    src={inviteDetails.images[imgType]}
                                                    alt={imgType}
                                                    sx={{ maxWidth: '100%', maxHeight: '150px', width: 'auto', height: 'auto', border: '1px solid #ccc', mb: 1, borderRadius: 1 }}
                                                />
                                            </>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">No Image Provided</Typography>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Box style={{ paddingTop: '2rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                    {props.data?.status === 'verified' ? (
                        <Button style={buttonStyle} onClick={handleDownloadPDF} disabled={isLoading}>
                            Download
                        </Button>
                    ) : (
                        <>
                            <Button 
                                style={isActionDisabled ? { ...buttonStyle, opacity: 0.6, cursor: 'not-allowed' } : { ...buttonStyle, backgroundColor: red }} 
                                disabled={isActionDisabled} 
                                onClick={() => setRejectDialogOpen(true)}
                            >
                                Reject
                            </Button>
                            <Button 
                                style={isActionDisabled ? { ...buttonStyle, opacity: 0.6, cursor: 'not-allowed' } : buttonStyle} 
                                disabled={isActionDisabled} 
                                onClick={() => setAcceptDialogOpen(true)}
                            >
                                Accept
                            </Button>
                        </>
                    )}
                </Box>
            </DialogContent>
        </Dialog>

            {/* Accept Confirmation Dialog */}
            <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={titleStyle}>Confirm</DialogTitle>
                <DialogContent sx={{ p: 3, mt: 2 }}>
                    <Typography>Are you sure you want to accept this registration? This will create a new WORKer.</Typography>
                    <Box style={{ paddingTop: '2rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <Button style={{ ...buttonStyle, backgroundColor: red }} onClick={() => setAcceptDialogOpen(false)}>Cancel</Button>
                        <Button style={buttonStyle} onClick={handleAcceptConfirm}>Confirm</Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Reject Confirmation Dialog */}
            <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle style={{ ...titleStyle, backgroundColor: headingBlue }}>Confirm</DialogTitle>
                <DialogContent sx={{ p: 3, mt: 2 }}>
                    <Typography sx={{ mb: 2 }}>Are you sure you want to reject this registration? Please provide a reason:</Typography>
                    <TextField fullWidth multiline rows={3} variant="outlined" placeholder="Reason for rejection..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                    <Box style={{ paddingTop: '2rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <Button style={buttonStyle} onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
                        <Button style={{ ...buttonStyle, backgroundColor: red }} onClick={handleRejectConfirm}>Confirm</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RegistrationInviteDetails;
